import { Input, TimelineBar, useDebouncedValue } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect, useMemo } from "react";
import { formatDate, formatName } from "@tellescope/utils";
import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";

export interface MoveToDiffProfileContentProps {
  currentEnduserId: string;
  onMove: (enduserId: string) => void;
  targetType: string;
}

export const MoveToDiffProfileContent = ({ currentEnduserId, onMove, targetType }: MoveToDiffProfileContentProps) => {
  const { t } = useTranslation("timeline");
  const [search, setSearch] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string>();

  const [debouncedSearch] = useDebouncedValue(search);
  const lowerDebouncedSearch = debouncedSearch.toLowerCase();
  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 100,
      search: { query: lowerDebouncedSearch },
    },
    enabled: !!lowerDebouncedSearch,
    select: (data) => {
      return data.pages.reduce<TellescopeModelFromName<"endusers">[]>((acc, page) => {
        acc.push(...page.filter((enduser) => enduser.id !== currentEnduserId));
        return acc;
      }, []);
    },
  });
  const filteredEndusers = useMemo(
    () => endusersQuery.data?.filter((enduser) => enduser.id !== currentEnduserId),
    [currentEnduserId, endusersQuery.data]
  );

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Use setTimeout to allow dropdown menu to fully close before focusing
    // Dropdown menus restore focus to their trigger after closing
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 175);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <div>
        <div className="space-y-4 p-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{t("Search Contacts")}</label>
            <Input
              ref={inputRef}
              type="text"
              placeholder={t("Search by name, email, or role...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </div>
          <div
            className={`space-y-2 relative ${
              endusersQuery.isLoading ? "opacity-50" : "opacity-100"
            } transition-all overflow-hidden`}
          >
            {filteredEndusers !== undefined ? (
              <>
                <label className="text-sm font-medium text-gray-700">
                  {t("Select Contact ")}
                  {!!search && !!filteredEndusers && (
                    <>
                      <span>{"(" + filteredEndusers.length + (filteredEndusers.length === 50 ? "+ " : " ")}</span>
                      {t("found")}
                      <span>{")"}</span>
                    </>
                  )}
                </label>
                {filteredEndusers.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <p className="text-sm">{t('No contacts found matching "{{search}}"', { search })}</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg max-h-64 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <div className="divide-y divide-gray-100">
                        {filteredEndusers.map((contact) => (
                          <div
                            key={contact.id}
                            className={`p-3 cursor-pointer transition-colors ${
                              selectedContactId === contact.id ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-gray-50"
                            }`}
                            onClick={() => setSelectedContactId((id) => (id === contact.id ? undefined : contact.id))}
                          >
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <div className="font-medium text-sm text-gray-900">{formatName(contact)}</div>
                                <div className="text-xs text-gray-500">{contact.email}</div>
                              </div>
                              {!!contact.dateOfBirth && (
                                <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                  {formatDate(contact.dateOfBirth)}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">{t("Start typing to search for contacts")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {!!selectedContactId && (
        <TimelineBar
          title={t("Moving to profile")}
          primaryAction={{
            label: t("timeline.moveToDiffContact.move", "Move " + targetType),
            onClick: () => onMove(selectedContactId),
          }}
          secondaryActions={[
            {
              label: t("Cancel"),
              onClick: () => setSelectedContactId(undefined),
            },
          ]}
        />
      )}
    </>
  );
};
